import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { C1_AppHeader } from "../../ConceptOne/src/components/C1_AppHeader";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import { itx } from "../translationItx/itx";
import { askToSaveProjectBeforeLeaving } from "./dialogs/SaveProjectBeforeLeaving";
import "../../ConceptOne/src/C1_styles.scss";
import "./MainAppHeader.scss";
import { AppHeaderProjectName } from "./AppHeaderProjectName";

type FullscreenDocument = Document & {
  mozFullScreenElement?: Element;
  webkitFullscreenElement?: Element;
  msFullscreenElement?: Element;
  mozCancelFullScreen?: () => void | Promise<void>;
  webkitExitFullscreen?: () => void | Promise<void>;
  msExitFullscreen?: () => void | Promise<void>;
};

function isAppFullscreen(): boolean {
  const doc = document as FullscreenDocument;
  return !!(doc.fullscreenElement ?? doc.mozFullScreenElement ?? doc.webkitFullscreenElement ?? doc.msFullscreenElement);
}

function exitAppFullscreen(): boolean {
  if (!isAppFullscreen()) return false;
  const doc = document as FullscreenDocument;
  const exit = doc.exitFullscreen ?? doc.mozCancelFullScreen ?? doc.webkitExitFullscreen ?? doc.msExitFullscreen;
  if (exit) {
    try {
      void Promise.resolve(exit.call(doc)).catch(() => {});
    } catch {
      // Keep the header collapsed if the browser cannot leave fullscreen yet.
    }
  }
  return true;
}

function requestAppFullscreen(): void {
  if (isAppFullscreen()) return;
  const element = document.documentElement as HTMLElement & {
    mozRequestFullScreen?: () => void | Promise<void>;
    webkitRequestFullscreen?: () => void | Promise<void>;
    msRequestFullscreen?: () => void | Promise<void>;
  };
  const request = element.requestFullscreen ?? element.mozRequestFullScreen
    ?? element.webkitRequestFullscreen ?? element.msRequestFullscreen;
  if (!request) return;
  try {
    // Request directly in the click event; hiding the header also works if fullscreen is denied.
    void Promise.resolve(request.call(element)).catch(() => {});
  } catch {
    // Older browser implementations may throw synchronously.
  }
}

export default function MainAppHeader() {
  useOnChangeWatcher(state.projectMetadata);
  const [collapsed, setCollapsed] = useState(false);
  return (
    <CollapsibleAppHeader
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      product="KNX-Engineering" // no itx
      projectName={state.projectMetadata.name}
      onProjectNameChange={
        state.projectMetadata.readOnly
          ? undefined
          : (name) => {
              state.projectMetadata.name = name;
              state.undoManager.userInteractionEnded();
            }
      }
      onProductClick={() => askToSaveProjectBeforeLeaving("/")}
      logoutTarget="/"
    />
  );
}

export function CollapsibleAppHeader({ collapsed, onCollapsedChange, product, projectName,
  onProjectNameChange, onProductClick, logoutTarget, children, idPrefix = "main-app-header", className = "" }: {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  product: string;
  projectName: string;
  onProjectNameChange?: (name: string) => void;
  onProductClick: () => void;
  logoutTarget?: string;
  children?: ReactNode;
  idPrefix?: string;
  className?: string;
}) {
  const hideButton = useRef<HTMLButtonElement>(null);
  const showButton = useRef<HTMLButtonElement>(null);
  const [fullscreen, setFullscreen] = useState(isAppFullscreen);
  useEffect(() => {
    const update = () => setFullscreen(isAppFullscreen());
    const events = [
      "fullscreenchange", // no itx
      "webkitfullscreenchange", // no itx
      "mozfullscreenchange", // no itx
      "MSFullscreenChange", // no itx
    ];
    events.forEach(event => document.addEventListener(event, update));
    return () => events.forEach(event => document.removeEventListener(event, update));
  }, []);
  const hideLabel = itx("mainAppHeader.hideAndFullscreen", "App-Header ausblenden und Vollbild aktivieren");
  const showLabel = fullscreen
    ? itx("mainAppHeader.exitFullscreen", "Vollbild beenden")
    : itx("mainAppHeader.show", "App-Header einblenden");

  function toggle(next: boolean) {
    if (!next && exitAppFullscreen()) return;
    if (next) requestAppFullscreen();
    onCollapsedChange(next);
    requestAnimationFrame(() => (next ? showButton : hideButton).current?.focus());
  }

  return (
    <div className={`C1 mainAppHeader ${className}${collapsed ? " mainAppHeader--collapsed" : ""}`}>
      <div
        id={idPrefix}
        className="mainAppHeaderPanel"
        data-testid={idPrefix}
        aria-hidden={collapsed}
        ref={(element) => { if (element) element.inert = collapsed; }}
      >
        <C1_AppHeader
          logoutTarget={logoutTarget}
          onProductClick={onProductClick}
          title={
            <>
              <span className="mainAppHeaderProduct">{product}</span>
              <AppHeaderProjectName name={projectName} onChange={onProjectNameChange} idPrefix={idPrefix} />
              <button
                ref={hideButton}
                type="button"
                className="mainAppHeaderToggle"
                data-testid={`${idPrefix}-hide`}
                aria-label={hideLabel}
                title={hideLabel}
                aria-expanded={!collapsed}
                aria-controls={idPrefix}
                onClick={() => toggle(true)}
              >
                <ChevronUp size={18} aria-hidden="true" />
              </button>
            </>
          }
        >
          {!collapsed && children}
        </C1_AppHeader>
      </div>
      <div className="mainAppHeaderReveal" aria-hidden={!collapsed}>
        <button
          ref={showButton}
          type="button"
          className="mainAppHeaderToggle"
          data-testid={`${idPrefix}-show`}
          aria-label={showLabel}
          title={showLabel}
          aria-expanded={!collapsed}
          aria-controls={idPrefix}
          tabIndex={collapsed ? 0 : -1}
          onClick={() => toggle(false)}
        >
          <ChevronDown size={18} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
